import { Injectable } from '@angular/core';
import { GuardsCheckStart, NavigationEnd, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { TimerObservable } from 'rxjs/observable/TimerObservable';

import { AuthenticationInterceptor } from './authentication-interceptor';
import { AuthenticationService } from './authentication.service';
import { CartService } from '../cart/cart.service';
import { environment } from './../../environments/environment';
import { EventService } from './event.service';
import { PublicAccessTerminalService } from './public-access-terminal.service';

const session_timeout_duration_min = Number(environment.sessionTimeout) || 10; // minutes

const session_timeout_duration_ms = session_timeout_duration_min * // minutes
                                    60 * // seconds in a minute
                                    1000; // ms in a second
@Injectable()
export class SessionTimeoutService {

  private subscription: Subscription;
  private activated = false;
  private tokenExpirationSubscription: Subscription;

  constructor(private authenticationInterceptor: AuthenticationInterceptor,
              private authenticationService: AuthenticationService,
              private cartService: CartService,
              private eventService: EventService,
              private modalService: BsModalService,
              private publicAccessTerminalService: PublicAccessTerminalService,
              private router: Router) { }

  initialize() {
    this.registerEventListeners(['mousemove', 'mousedown', 'keypress', 'touchmove', 'scroll']);
    console.log(`timeout value in minutes = ${session_timeout_duration_min}| timeout orig value = ${environment.sessionTimeout}`);

    this.authenticationService.tokenExpirationChange$.subscribe((value) => {
       if (value) {
         this.setTokenExpirationCountdown(value);
       } else {
         this.cancelTokenExpirationCountdown();
       }
     });

     this.setUpSessionTimeoutEventListeners();
  }

  start(): void {
    this.activated = true;
    this.restartCountdown();
  }
  stop(): void {
    this.cancelCoundown();
    this.activated = false;
  }

  private registerEventListeners(eventNames: string[]) {
    for (let eventName of eventNames) {
      document.addEventListener(eventName, (event) => {
        if (!this.activated) {
          return;
        }
        this.restartCountdown();
      }, false);
    }
  }

  private restartCountdown(): void {
    this.cancelCoundown();

    const timer = TimerObservable.create(session_timeout_duration_ms);
    this.subscription = timer.subscribe(t => this.eventService.notifyOfImpendingSessionTimeout());
  }

  private cancelCoundown(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private setTokenExpirationCountdown(expirationMs: number): void {
    const warningDuration = 60 * 1000; // 1 minute
    const timerDuration = expirationMs > warningDuration ? expirationMs - warningDuration : expirationMs;
    const timer = TimerObservable.create(timerDuration);
    this.tokenExpirationSubscription = timer.subscribe(t => {
      if (this.publicAccessTerminalService.isPublicAccessTerminal()) {
        this.authenticationInterceptor.refreshToken()
          .subscribe((success) => {
            if (!success) {
              this.router.navigateByUrl('/public_access_terminal');
              }
          });
      }
      this.tokenExpirationSubscription.unsubscribe();
    });
  }
  private cancelTokenExpirationCountdown(): void {
    if (this.tokenExpirationSubscription) {
      this.tokenExpirationSubscription.unsubscribe();
    }
  }

  private setUpSessionTimeoutEventListeners(): void {
    this.router.events
      .filter(event => event instanceof NavigationEnd)
      .map(event => event as NavigationEnd) // done just to get type assertion to work below
      .subscribe(event  => {

        // when hitting /public_access_terminal, the user may or may not be a PAT based on timing/whether they're resetting
        if (event.urlAfterRedirects.indexOf('/public_access_terminal') !== -1) {
          this.stop();
          return;
        }

        if (this.publicAccessTerminalService.isPublicAccessTerminal()) {
          // Stop the timer if navigating to the landing page with an empty cart, otherwise start it
          if (event.urlAfterRedirects === '/' && this.cartService.isEmpty()) {
            this.stop();
          } else if (event.urlAfterRedirects !== '/') {
              this.start();
          }
        } else { // Not a PAT
          if (event.urlAfterRedirects.indexOf('/logout') !== -1) {
            if (this.authenticationService.isUserLoggedIn()) {
              this.start();
            } else {
              this.stop();
            }
          } else {
              this.start();
            }
        }
      });

    // If the PAT starts up a route guard (like the civil disclaimer), start the inactivity timer
    this.router.events
      .filter(event => event instanceof GuardsCheckStart)
      .subscribe(event => {
        if (this.publicAccessTerminalService.isPublicAccessTerminal()) {
            this.start();
        }
      });

      // PATs need to start the inactivity timer when a modal is opened from the landing page
    this.modalService.onShown.subscribe(() => {
      if (this.publicAccessTerminalService.isPublicAccessTerminal() &&
          this.router.url === '/') {
            this.start();
          }
    })
    // PATs need to stop the inactivity timer when a modal is closed on the landing page with an empty cart
    this.modalService.onHidden.subscribe(() => {
      if (this.publicAccessTerminalService.isPublicAccessTerminal() &&
          this.router.url === '/' &&
          this.cartService.isEmpty()) {
            this.stop();
          }
    })
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/shared/session-timeout.service.ts